import React from 'react';
import { ecosystemData } from '../data/mock';
import { Cpu, Database, Settings } from 'lucide-react';
import useScrollAnimation from '../hooks/useScrollAnimation';
import useParallax from '../hooks/useParallax';

const iconMap = {
  Cpu: Cpu,
  Database: Database,
  Settings: Settings,
};

const EcosystemSection = () => {
  const [headerRef, headerVisible] = useScrollAnimation({ threshold: 0.2 });
  const [itemsRef, itemsVisible] = useScrollAnimation({ threshold: 0.15 });
  
  // Parallax effect
  const parallaxBg = useParallax(0.06, 'down');

  return (
    <section className="bg-[#FDF8FA] py-24 relative">
      {/* Decorative Elements */}
      <div className="absolute top-20 left-20 w-48 h-48 bg-[#3D4B8C]/5 rounded-full blur-3xl" />
      <div className="absolute bottom-20 right-20 w-64 h-64 bg-[#C5366A]/5 rounded-full blur-3xl" />

      <div className="max-w-[1400px] mx-auto px-[7.6923%] relative z-10">
        <div dir="rtl" className="text-right">
          {/* Section Header */}
          <div 
            ref={headerRef}
            className={`text-center mb-16 transition-all duration-700 ${
              headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <p className="text-[#C5366A] text-base font-medium mb-4 tracking-wide">
              למה איתנו?
            </p>
            <h2 className="text-[#2D2D2D] text-3xl md:text-4xl lg:text-5xl font-semibold mb-6">
              {ecosystemData.headline}
            </h2>
            <p className="text-[#6B6B6B] text-lg md:text-xl max-w-2xl mx-auto">
              {ecosystemData.description}
            </p>
          </div>

          {/* Ecosystem Items */}
          <div ref={itemsRef} className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {ecosystemData.items.map((item, index) => {
              const IconComponent = iconMap[item.icon];
              return (
                <div
                  key={index}
                  className={`relative p-8 border border-[#C5366A]/10 bg-white rounded-xl hover:border-[#C5366A]/30 hover:shadow-xl transition-all duration-700 ease-out group ${
                    itemsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                  }`}
                  style={{ transitionDelay: itemsVisible ? `${index * 150}ms` : '0ms' }}
                >
                  {/* Number */}
                  <span className="absolute top-6 left-6 text-[#C5366A]/10 text-7xl font-bold">
                    {String(index + 1).padStart(2, '0')}
                  </span>

                  {/* Icon */}
                  <div className="relative z-10 mb-6">
                    <div className="w-14 h-14 bg-[#C5366A]/10 rounded-lg flex items-center justify-center">
                      <IconComponent className="w-7 h-7 text-[#C5366A]" />
                    </div>
                  </div>

                  {/* Title */}
                  <h3 className="relative z-10 text-[#2D2D2D] text-xl font-semibold mb-4">
                    {item.title}
                  </h3>

                  {/* Description */}
                  <p className="relative z-10 text-[#6B6B6B] text-base leading-relaxed">
                    {item.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default EcosystemSection;
